import { useState, useEffect, useCallback } from 'react';

export type CurrencyCode = 'USD' | 'AUD';

export interface CurrencyInfo {
  code: CurrencyCode;
  symbol: string;
  label: string;
  flag: string;
}

export const CURRENCIES: CurrencyInfo[] = [
  { code: 'USD', symbol: '$', label: 'USD', flag: '🇺🇸' },
  { code: 'AUD', symbol: 'A$', label: 'AUD', flag: '🇦🇺' },
];

const CURRENCY_KEY = 'peptide_currency';

function getInitialCurrency(): CurrencyCode {
  try {
    const params = new URLSearchParams(window.location.search);
    const urlCurrency = params.get('currency');
    if (urlCurrency) {
      const upper = urlCurrency.toUpperCase();
      if (CURRENCIES.some(c => c.code === upper)) {
        localStorage.setItem(CURRENCY_KEY, upper);
        return upper as CurrencyCode;
      }
    }
  } catch {
    // fall through to localStorage
  }
  try {
    return (localStorage.getItem(CURRENCY_KEY) as CurrencyCode) || 'USD';
  } catch {
    return 'USD';
  }
}

export function useCurrency() {
  const [currency, setCurrencyState] = useState<CurrencyCode>(getInitialCurrency);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyState(code);
    localStorage.setItem(CURRENCY_KEY, code);
  }, []);

  const info = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

  const formatPrice = useCallback((price: number) => {
    return `${info.symbol}${Math.round(price).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }, [info.symbol]);

  const getShareUrl = useCallback((code: CurrencyCode) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('currency', code);
      return url.toString();
    } catch {
      return `${window.location.origin}${window.location.pathname}?currency=${code}`;
    }
  }, []);

  return {
    currency,
    setCurrency,
    info,
    formatPrice,
    getShareUrl,
    CURRENCIES,
  };
}